import { deleteTeam } from '@gonfalon/openapi/operations/deleteTeam';
import { getTeam, type GetTeamQueryParams } from '@gonfalon/openapi/operations/getTeam';
import { getTeamRoles } from '@gonfalon/openapi/operations/getTeamRoles';
import { getTeams, type GetTeamsQueryParams } from '@gonfalon/openapi/operations/getTeams';
import { patchTeam } from '@gonfalon/openapi/operations/patchTeam';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

import { getMemberAccessSummaryQuery, getTeamAccessSummaryQuery } from './accessSummaries';

export const getTeamsQuery = createQueryOptions(getTeams);
export const getInfiniteTeamsQuery = createInfiniteQueryOptions(getTeams);
export const getTeamQuery = createQueryOptions(getTeam);
export const getTeamRolesQuery = createQueryOptions(getTeamRoles);

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const teamsKeys = createQueryKeys('teams', {
  list: ({ params }: { params?: GetTeamsQueryParams }) => ({
    queryKey: params ? ['collection', params] : ['collection'],
    queryFn: async ({ signal }) => reactQueryResponseAdapter(getTeams({ query: params }, { signal })),
  }),
  detail: ({ teamKey, query }: { teamKey: string; query?: GetTeamQueryParams }) => ({
    queryKey: [teamKey, query],
    queryFn: async ({ signal }) => reactQueryResponseAdapter(getTeam({ teamKey, query }, { signal })),
  }),
});

export const teamsList = teamsKeys.list;
export const teamsDetail = teamsKeys.detail;
export const teams = teamsKeys;

export const usePatchTeam = createMutationHook(patchTeam, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getTeamQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: teamsKeys.list._def }),
      queryClient.invalidateQueries({ queryKey: getTeamRolesQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getMemberAccessSummaryQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getTeamAccessSummaryQuery.partialQueryKey() }),
    ]);
  },
});

export const useDeleteTeam = createMutationHook(deleteTeam, {
  onSuccess: async (_, { teamKey }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: teamsKeys.detail({ teamKey }).queryKey });
    await queryClient.invalidateQueries({ queryKey: teamsKeys.list._def });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getMemberAccessSummaryQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getTeamAccessSummaryQuery.partialQueryKey() }),
    ]);
  },
});
